/**
 * Login options from the Login menu. Business/private **clients** use `/login/clients` but
 * are not allowed to create assets in the UI.
 */
export const RESTRICTED_CLIENT_USER_TYPES = ['business client', 'private client'] as const;

export function isClientUserType(loginType: string | null | undefined): boolean {
  if (!loginType) {
    return false;
  }
  return (RESTRICTED_CLIENT_USER_TYPES as readonly string[]).includes(loginType);
}

export function isBusinessWorkerLogin(loginType: string | null | undefined): boolean {
  return loginType === 'business worker';
}

/**
 * Roles that may browse the public catalog via `POST /search/assets`.
 * Includes asset owners (home / type listings); owners still manage only their own
 * assets on the Assets tab (`canMutateAssets`).
 * Business workers share client login (`login/clients`) but are not business/private clients.
 */
export function canBrowseBookableAssets(loginType: string | null | undefined): boolean {
  return (
    isClientUserType(loginType) ||
    isBusinessWorkerLogin(loginType) ||
    isAssetOwnerLoginType(loginType)
  );
}

/** Asset owners manage assets; clients and business workers browse bookable listings only. */
export function canUseAssetsMenu(loginType: string | null | undefined): boolean {
  return isAssetOwnerLoginType(loginType) || canBrowseBookableAssets(loginType);
}

/** Business/private asset owners from the Login menu (`business asset owner`, `private asset owner`). */
export function isAssetOwnerLoginType(loginType: string | null | undefined): boolean {
  if (!loginType) {
    return false;
  }
  return /asset owner/i.test(loginType);
}

/** Only asset owners may create/manage assets — clients and business workers cannot. */
export function canCreateAssets(loginType: string | null | undefined): boolean {
  return isAssetOwnerLoginType(loginType);
}

/** Alias for browse vs owner asset tooling (create, update, delete, owner list routes). */
export function canMutateAssets(loginType: string | null | undefined): boolean {
  return canCreateAssets(loginType);
}

/** Asset owners cannot book assets; only private clients and business workers may create bookings. */
export function canBookAssets(loginType: string | null | undefined): boolean {
  if (!loginType) {
    return false;
  }
  return loginType === 'private client' || isBusinessWorkerLogin(loginType);
}

/** Header bell: same upcoming-booking reminders for bookers and asset owners. */
export function canLoadBookingNotifications(loginType: string | null | undefined): boolean {
  return canBookAssets(loginType) || isAssetOwnerLoginType(loginType);
}

export function isBusinessClientLogin(loginType: string | null | undefined): boolean {
  return loginType === 'business client';
}

/**
 * Business workers may register publicly with an employer code, or while signed in as the
 * creating business client (JWT links the worker without a code).
 */
export function canSelectBusinessWorkerRegisterType(
  isLoggedIn: boolean,
  loginType: string | null,
): boolean {
  if (!isLoggedIn) {
    return true;
  }
  return isBusinessClientLogin(loginType);
}

/** True when POST `/register/BusinessWorker` should send the business-client session JWT. */
export function businessWorkerRegisterUsesBusinessClientSession(
  isLoggedIn: boolean,
  loginType: string | null,
): boolean {
  return isLoggedIn && isBusinessClientLogin(loginType);
}

/** Display label for the top bar (title case). */
export function formatUserTypeLabel(raw: string): string {
  return raw
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
